import { mergeTopicContent, TOPIC_ENTRY_KIND_BLOCK, TOPIC_ENTRY_KIND_CONTAINER } from '../../courses/topicContent';
import { TOPIC_BLOCK_EDITOR_REGISTRY } from '../../courses/topicBlockEditors';
import { getTopicBlockType } from '../../courses/topicBlockTypes';
import { BlockTypeBadge } from '../../courses/preview/blockTypeIcons';
import { mergeBlocks, BLOCK_KIND_CONTAINER, BLOCK_KIND_INTERACTIVE } from '../../exercise-templates/blockList';
import { getBlockType } from '../../../variables/exerciseBlockTypes';
import BLOCK_EDITOR_REGISTRY from '../../exercise-templates/block-editors/registry';
import EmbedConversationPreview from '../../courses/preview/EmbedConversationPreview';
import BlurredPreviewWrapper from '../../courses/preview/BlurredPreviewWrapper';
import ContainerLayout from '../../../components/ContainerLayout';
import { safeParseContainerConfig } from '../../../variables/containerLayoutTypes';
import ConversationStepHistory from './ConversationStepHistory';
import ExerciseAnswerState from './ExerciseAnswerState';
import NotePin from './NotePin';
import { getAnsweredComponent } from './AnsweredInteractivePreview';
import AnsweredEmbedGrid from './AnsweredEmbedGrid';
import TopicLessonFlyout from './TopicLessonFlyout';
import BlockMarkerSlot from './BlockMarkerSlot';
import { useNavigate } from 'react-router-dom';
import IconCanvasOpen from '../../../components/icons/IconCanvasOpen';
import language from '../../../variables/language';
import '../../../css/topic-canvas.css';

// This whole page is a read-only activity REVIEW, never an editing surface
// - a teacher must never be able to answer on the student's behalf, even
// accidentally. multiple_choice/true_false/fill_blank/matching therefore
// ALWAYS render through AnsweredInteractivePreview.jsx (disabled inputs,
// no onClick handlers at all), never courses/preview/interactivePreviews.jsx's
// live clickable simulator - regardless of whether the student has actually
// answered yet (an unanswered AnsweredComponent just shows nothing picked,
// still genuinely inert). `ordering` is answerable too now (drag items into
// numbered slots, resultJson {placed}) and goes through AnsweredOrdering like
// the rest. embed_conversation is handled separately,
// directly in ExerciseBlockEntry below (it needs progressByElementId, which
// this function doesn't receive). Every render path here is additionally
// wrapped in .workspace-frozen-content (see the call sites below) as a
// second, CSS-level line of defense (pointer-events:none) covering any
// block type that might slip through this dispatch or gain real
// interactivity later. embed_grid ALSO now shows the student's real saved
// progress (which cards flipped / typed answers / letters placed / pairs
// matched - AnsweredEmbedGrid.jsx, shapes verified straight from
// english-app's own 4 game components) instead of the admin's blank
// live-play preview - same spirit as every other answerable type here,
// just needing its own cards fetch since GridCard[] isn't embedded on the
// block the way config is for the rest.
function renderExerciseBlockContent(block, resultJson) {
   if (block.type === 'embed_grid') return <AnsweredEmbedGrid block={block} resultJson={resultJson} />;
   const AnsweredComponent = getAnsweredComponent(block.type);
   if (AnsweredComponent) return <AnsweredComponent block={block} resultJson={resultJson} />;
   const registryEntry = BLOCK_EDITOR_REGISTRY[block.type];
   return registryEntry ? registryEntry.renderPreview(block) : null;
}

// One TopicElement (text/media) - the plain content path, no activity
// overlay possible (only interactive exercise elements are answerable).
function TopicBlockEntry({ block, notes, workspaceId, studentId, onNotesChanged }) {
   const registryEntry = TOPIC_BLOCK_EDITOR_REGISTRY[block.type];
   if (!registryEntry) return null;
   const blockType = getTopicBlockType(block.type) || {};
   return (
      <div className='course-preview-block'>
         <BlockTypeBadge type={block.type} accent={blockType.accent} label={blockType.label} />
         <BlurredPreviewWrapper elementType='TOPIC' elementId={block.id} blurEnabled={!!block.blurEnabled} studentId={studentId}>
            <div className='course-preview-block-content'>
               <div className='workspace-frozen-content'>{registryEntry.renderPreview(block)}</div>
            </div>
         </BlurredPreviewWrapper>
         <NotePin notes={notes} kind='topicElement' targetId={block.id} workspaceId={workspaceId} onNotesChanged={onNotesChanged} />
      </div>
   );
}

function TopicContainerGroup({ container, elements, notesByTarget, workspaceId, studentId, onNotesChanged }) {
   const children = mergeTopicContent({ elements, containers: [], containerId: container.id });
   if (children.length === 0) return null;
   const config = safeParseContainerConfig(container.config, container.layout);
   return (
      <ContainerLayout layout={container.layout} config={config}>
         {children.map(entry => (
            <div className='course-preview-container-item' key={entry.item.id}>
               <TopicBlockEntry block={entry.item} notes={notesByTarget['topicElement:' + entry.item.id] || []}
                  workspaceId={workspaceId} studentId={studentId} onNotesChanged={onNotesChanged} />
            </div>
         ))}
      </ContainerLayout>
   );
}

// embed_conversation gets its own dedicated branch: when the student has
// real recorded steps, the window shows ONLY the real Q&A history
// (ConversationStepHistory, paginated with a correct/wrong count) - the
// generic "representative path" admin preview is deliberately NOT also
// shown alongside it (would just be a second, fake, redundant version of
// the same window). Wrapped in the same .course-preview-embed-conversation
// class the raw preview uses, so ConversationStepHistory's own
// .course-preview-question card picks up the exact same "window" framing
// (background/border/radius) via the existing nested-selector CSS rule,
// per explicit user request to keep it looking like one cohesive widget.
// Only falls back to the frozen generic preview when there's nothing real
// to show yet (student hasn't started this conversation).
function ConversationBlockEntry({ block, notesByTarget, progressByElementId, workspaceId, studentId, onNotesChanged }) {
   const blockType = getBlockType(block.type) || {};
   const progressEntry = progressByElementId[block.id];
   const hasSteps = progressEntry && progressEntry.steps && progressEntry.steps.length > 0;

   return (
      <div className='course-preview-block'>
         <BlockTypeBadge type={block.type} accent={blockType.accent} label={blockType.label} />
         <BlurredPreviewWrapper elementType='EXERCISE_INTERACTIVE' elementId={block.id} blurEnabled={!!block.blurEnabled} studentId={studentId}>
            <div className='course-preview-block-content'>
               <div className='course-preview-embed-conversation'>
                  {hasSteps ? (
                     <ConversationStepHistory progress={progressEntry.progress} steps={progressEntry.steps} />
                  ) : (
                     <>
                        <div className='workspace-frozen-content'><EmbedConversationPreview block={block} /></div>
                        <p className='workspace-activity-empty-hint'>Студент ще не почав цю розмову.</p>
                     </>
                  )}
               </div>
            </div>
         </BlurredPreviewWrapper>
         <NotePin notes={notesByTarget['exerciseInteractiveElement:' + block.id] || []}
            kind='exerciseInteractiveElement' targetId={block.id} workspaceId={workspaceId} onNotesChanged={onNotesChanged} />
      </div>
   );
}

// One exercise element (static content OR an answerable interactive one) -
// this is where activity actually shows up: every answerable type gets its
// current saved-state summary, static content gets neither (nothing to
// answer). embed_conversation is dispatched to ConversationBlockEntry above
// instead (different enough shape - its own window, no separate
// ExerciseAnswerState line - to not force into this same layout).
function ExerciseBlockEntry({ block, notesByTarget, progressByElementId, savedStateByElementId, workspaceId, studentId, onNotesChanged }) {
   if (block.type === 'embed_conversation') {
      return <ConversationBlockEntry block={block} notesByTarget={notesByTarget} progressByElementId={progressByElementId}
         workspaceId={workspaceId} studentId={studentId} onNotesChanged={onNotesChanged} />;
   }

   const blockType = getBlockType(block.type) || {};
   const savedState = savedStateByElementId[block.id];
   const content = renderExerciseBlockContent(block, savedState && savedState.resultJson);
   if (!content) return null;

   return (
      <div className='course-preview-block'>
         <BlockTypeBadge type={block.type} accent={blockType.accent} label={blockType.label} />
         <BlurredPreviewWrapper elementType='EXERCISE_INTERACTIVE' elementId={block.id} blurEnabled={!!block.blurEnabled} studentId={studentId}>
            <div className='course-preview-block-content'>
               <div className='workspace-frozen-content'>{content}</div>
               <ExerciseAnswerState savedState={savedState} />
            </div>
         </BlurredPreviewWrapper>
         <NotePin notes={notesByTarget['exerciseInteractiveElement:' + block.id] || []}
            kind='exerciseInteractiveElement' targetId={block.id} workspaceId={workspaceId} onNotesChanged={onNotesChanged} />
      </div>
   );
}

function ExerciseStaticBlockEntry({ block, notesByTarget, workspaceId, studentId, onNotesChanged }) {
   const blockType = getBlockType(block.type) || {};
   const registryEntry = BLOCK_EDITOR_REGISTRY[block.type];
   const content = registryEntry ? registryEntry.renderPreview(block) : null;
   if (!content) return null;
   return (
      <div className='course-preview-block'>
         <BlockTypeBadge type={block.type} accent={blockType.accent} label={blockType.label} />
         <BlurredPreviewWrapper elementType='EXERCISE' elementId={block.id} blurEnabled={!!block.blurEnabled} studentId={studentId}>
            <div className='course-preview-block-content'>
               <div className='workspace-frozen-content'>{content}</div>
            </div>
         </BlurredPreviewWrapper>
         <NotePin notes={notesByTarget['exerciseElement:' + block.id] || []}
            kind='exerciseElement' targetId={block.id} workspaceId={workspaceId} onNotesChanged={onNotesChanged} />
      </div>
   );
}

function ExerciseContainerGroup({ container, elements, interactiveElements, notesByTarget, progressByElementId, savedStateByElementId, workspaceId, studentId, onNotesChanged }) {
   const children = mergeBlocks(elements, interactiveElements, [], container.id);
   if (children.length === 0) return null;
   const config = safeParseContainerConfig(container.config, container.layout);
   return (
      <ContainerLayout layout={container.layout} config={config}>
         {children.map(entry => (
            <div className='course-preview-container-item' key={entry.kind + '-' + entry.block.id}>
               {entry.kind === BLOCK_KIND_INTERACTIVE ? (
                  <ExerciseBlockEntry block={entry.block} notesByTarget={notesByTarget} progressByElementId={progressByElementId}
                     savedStateByElementId={savedStateByElementId} workspaceId={workspaceId} studentId={studentId} onNotesChanged={onNotesChanged} />
               ) : (
                  <ExerciseStaticBlockEntry block={entry.block} notesByTarget={notesByTarget} workspaceId={workspaceId} studentId={studentId} onNotesChanged={onNotesChanged} />
               )}
            </div>
         ))}
      </ContainerLayout>
   );
}

function ExerciseLinkSection({ exercise, notesByTarget, progressByElementId, savedStateByElementId, workspaceId, studentId, onNotesChanged }) {
   const merged = mergeBlocks(exercise.elements, exercise.interactiveElements, exercise.containers);
   if (!exercise.link.exerciseTemplate) return null;
   return (
      <div className='course-preview-exercise'>
         <h2 className='course-preview-exercise-title'>{exercise.link.exerciseTemplate.name}</h2>
         {merged.length === 0 ? (
            <p className='course-preview-empty-hint'>Ця вправа ще порожня.</p>
         ) : (
            <div className='course-preview-timeline'>
               {merged.map(entry => (
                  entry.kind === BLOCK_KIND_CONTAINER
                     ? <ExerciseContainerGroup key={'container-' + entry.block.id} container={entry.block}
                        elements={exercise.elements} interactiveElements={exercise.interactiveElements}
                        notesByTarget={notesByTarget} progressByElementId={progressByElementId}
                        savedStateByElementId={savedStateByElementId} workspaceId={workspaceId} studentId={studentId} onNotesChanged={onNotesChanged} />
                     : entry.kind === BLOCK_KIND_INTERACTIVE
                     ? <ExerciseBlockEntry key={'el-' + entry.block.id} block={entry.block}
                        notesByTarget={notesByTarget} progressByElementId={progressByElementId}
                        savedStateByElementId={savedStateByElementId} workspaceId={workspaceId} studentId={studentId} onNotesChanged={onNotesChanged} />
                     : <ExerciseStaticBlockEntry key={'el-' + entry.block.id} block={entry.block}
                        notesByTarget={notesByTarget} workspaceId={workspaceId} studentId={studentId} onNotesChanged={onNotesChanged} />
               ))}
            </div>
         )}
      </div>
   );
}

// One topic's full content + activity, in the same visual "timeline" shape
// as the admin Preview tab's TopicPreviewSection - blocks, containers and
// linked exercises interleaved in position order, each with a NotePin
// attached and (for anything answerable) its recorded activity shown
// inline rather than a generic "what this looks like" simulation.
export default function WorkspaceTopicSection({ topicEntry, notesByTarget, progressByElementId, savedStateByElementId, workspaceId, studentId, courseId, onNotesChanged, markersByTarget, onMarkersChanged, sectionRef }) {
   const { topic, elements, containers, exerciseLinks } = topicEntry;
   const topLevel = mergeTopicContent({ elements, containers, exerciseLinks: exerciseLinks.map(e => e.link), containerId: null });
   const navigate = useNavigate();
   const lang = language[language.lang];

   return (
      <div className='course-preview-topic-section workspace-topic-canvas-anchor' ref={sectionRef} data-topic-id={topic.id}>
         <span className='workspace-topic-canvas-trigger' title={lang.canvasOpen}
            onClick={() => navigate('/students/' + studentId + '/workspace/' + courseId + '/canvas/' + topic.id)}>
            <IconCanvasOpen style={{ width: 22, height: 22 }} />
         </span>
         <div className='course-preview-header'>
            <div className='course-preview-eyebrow-row'>
               <span className='course-preview-eyebrow'>Тема</span>
               <TopicLessonFlyout topicId={topic.id} studentId={studentId} />
            </div>
            <h1 className='course-preview-title'>{topic.name}</h1>
            {topic.description ? <p className='course-preview-description'>{topic.description}</p> : ''}
            <div className='course-preview-header-rule' />
         </div>

         {topLevel.length > 0 ? (
            <div className='course-preview-timeline'>
               {topLevel.map(entry => {
                  if (entry.kind === TOPIC_ENTRY_KIND_BLOCK) {
                     return (
                        <BlockMarkerSlot key={'marker-el-' + entry.item.id} kind='topicElement' targetId={entry.item.id}
                           marker={markersByTarget['topicElement:' + entry.item.id]} workspaceId={workspaceId} onChanged={onMarkersChanged}>
                           <TopicBlockEntry block={entry.item}
                              notes={notesByTarget['topicElement:' + entry.item.id] || []}
                              workspaceId={workspaceId} studentId={studentId} onNotesChanged={onNotesChanged} />
                        </BlockMarkerSlot>
                     );
                  }
                  if (entry.kind === TOPIC_ENTRY_KIND_CONTAINER) {
                     return (
                        <BlockMarkerSlot key={'marker-container-' + entry.item.id} kind='topicContainer' targetId={entry.item.id}
                           marker={markersByTarget['topicContainer:' + entry.item.id]} workspaceId={workspaceId} onChanged={onMarkersChanged}>
                           <TopicContainerGroup container={entry.item} elements={elements}
                              notesByTarget={notesByTarget} workspaceId={workspaceId} studentId={studentId} onNotesChanged={onNotesChanged} />
                        </BlockMarkerSlot>
                     );
                  }
                  const exercise = exerciseLinks.find(e => e.link.id === entry.item.id);
                  return exercise ? (
                     <BlockMarkerSlot key={'marker-ex-' + entry.item.id} kind='topicExerciseTemplate' targetId={entry.item.id}
                        marker={markersByTarget['topicExerciseTemplate:' + entry.item.id]} workspaceId={workspaceId} onChanged={onMarkersChanged}>
                        <ExerciseLinkSection exercise={exercise}
                           notesByTarget={notesByTarget} progressByElementId={progressByElementId}
                           savedStateByElementId={savedStateByElementId} workspaceId={workspaceId} studentId={studentId} onNotesChanged={onNotesChanged} />
                     </BlockMarkerSlot>
                  ) : null;
               })}
            </div>
         ) : (
            <p className='course-preview-empty-hint'>У цій темі ще немає контенту.</p>
         )}
      </div>
   );
}
